import type { QueryClient } from "@tanstack/react-query";
import { Outlet, createRootRouteWithContext, type ErrorComponentProps } from "@tanstack/react-router";
import Link from "@/components/link";
import { BrandLogo } from "@/components/brand-logo";
import { ThemeProvider } from "@/components/theme-provider";
import { ScrollbarAutohide } from "@/components/scrollbar-autohide";

/** Every page's search params are plain strings (?from=&to=&tab=), as the report pages read them. */
export type SearchParams = Record<string, string | undefined>;

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const out: SearchParams = {};
    for (const [k, v] of Object.entries(search)) if (typeof v === "string" && v !== "") out[k] = v;
    return out;
  },
  component: Root,
  notFoundComponent: NotFound,
  errorComponent: RootError,
});

function Root() {
  return (
    <ThemeProvider>
      <ScrollbarAutohide />
      <Outlet />
    </ThemeProvider>
  );
}

// A mistyped address: the same quiet card as the login page, with a way back.
function NotFound() {
  return (
    <div className="flex min-h-screen flex-1 items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-surface p-8 text-center shadow-sm">
        <BrandLogo className="mx-auto h-auto w-[200px] dark:hidden" />
        <span className="hidden dark:block">
          <BrandLogo variant="tile" className="w-[196px]" />
        </span>
        <h1 className="mt-6 font-heading text-lg font-bold text-foreground">That page is not here</h1>
        <p className="mt-2 text-sm text-muted">The address may have a typo, or the page has moved.</p>
        <Link href="/" className="mt-6 inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90">
          Back to the dashboard
        </Link>
      </div>
    </div>
  );
}

// The last resort when the shell itself fails. Plain styles: the theme and
// fonts may not have loaded.
function RootError({ reset }: ErrorComponentProps) {
  return (
    <div style={{ fontFamily: "system-ui, sans-serif", background: "#f6f5f9", color: "#231f2b", margin: 0, padding: "3rem 1rem", minHeight: "100vh" }}>
      <div style={{ maxWidth: 480, margin: "0 auto", background: "#fff", border: "1px solid #e4e0ea", borderRadius: 12, padding: 24 }}>
        <h1 style={{ fontSize: 20, margin: "0 0 8px" }}>The dashboard could not load</h1>
        <p style={{ fontSize: 14, color: "#6b6677", margin: "0 0 16px" }}>
          Something failed before the page could draw. Try again in a moment; if it keeps happening, tell Digital King.
        </p>
        <button
          type="button"
          onClick={reset}
          style={{ background: "#662d91", color: "#fff", border: 0, borderRadius: 8, padding: "8px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
        >
          Try again
        </button>
      </div>
    </div>
  );
}
